import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SlideOut } from '~/chords';
import type { SlideOutRef } from '~/chords/SlideOut/types';
import { Button } from '~/components/Button';
import { Dropdown } from '~/components/Dropdown';
import { Label } from '~/components/Label';
import { cn } from '~/lib/cn';
import { CheckboxTree } from '~/modules/label-perf/components/CheckboxTree';
import type { CheckboxTreeItem } from '~/modules/label-perf/components/CheckboxTree/types';
import { flattenCheckboxTree } from '~/modules/label-perf/components/CheckboxTree/utils';
import { useAvailableDropdownHeight } from '~/modules/label-perf/components/LabelFilter/useAvailableDropdownHeight';
import type {
  LabelData,
  LabelHierarchy,
  LabelPerfFilters,
} from '~/modules/label-perf/types';
import { mapLabelHierarchyToCheckboxTreeItem } from '~/modules/label-perf/utils/labels';

interface FilterPaneProps {
  originalLabels: LabelData[];
  labelHierarchyData: LabelHierarchy[] | undefined;
  isLoading: boolean;
  disabled?: boolean;
  onApplyFilters: (filters: LabelPerfFilters) => void;
  onClose?: () => void;
  panelRef: RefObject<SlideOutRef>;
}

const FORM_ID = 'filter-panel';

export function FilterPanel({
  onClose,
  onApplyFilters,
  originalLabels,
  labelHierarchyData,
  isLoading,
  disabled,
  panelRef,
}: FilterPaneProps) {
  const { t } = useTranslation('label-perf');
  const methods = useForm<LabelPerfFilters>({
    defaultValues: { labels: originalLabels },
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const labelFlatItems = watch('labels');
  const [labelTreeItems, setLabelTreeItems] = useState<CheckboxTreeItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { availableHeight } = useAvailableDropdownHeight(dropdownRef);

  const onSubmit: SubmitHandler<LabelPerfFilters> = (formData) => {
    onApplyFilters(formData);
    onClose?.();
  };

  useEffect(() => {
    if (labelHierarchyData?.length) {
      const originalLabelIds = new Set(
        originalLabels.map((label) => label.labelId),
      );

      setLabelTreeItems(
        mapLabelHierarchyToCheckboxTreeItem(
          labelHierarchyData,
          originalLabelIds,
        ),
      );
    }
  }, [labelHierarchyData, originalLabels]);

  useEffect(() => {
    if (isLoading || !labelTreeItems.length) {
      return;
    }

    const newLabelFlatItems = flattenCheckboxTree(labelTreeItems)
      .filter((label) => label.checked)
      .map((label) => ({
        labelId: parseInt(label.id, 10),
        name: label.label,
      }))
      .sort((a, b) => a.labelId - b.labelId);

    setValue('labels', newLabelFlatItems, { shouldDirty: true });
  }, [labelTreeItems, setValue, isLoading]);

  const handleLabelsTreeChange = useCallback((newItems: CheckboxTreeItem[]) => {
    setLabelTreeItems(newItems);
  }, []);

  return (
    <SlideOut ref={panelRef}>
      <SlideOut.Header>{t('body.filters')}</SlideOut.Header>
      <SlideOut.Body>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex size-full flex-col"
            id={FORM_ID}
          >
            <div className="flex min-h-0 grow flex-col">
              <Label className="mb-sm font-semibold">
                {t('body.labelRegion')}
              </Label>

              {isLoading ? (
                <div className="w-full" ref={dropdownRef}>
                  <Dropdown
                    options={[]}
                    placeholder={t('body.loading')}
                    disabled
                  >
                    <div data-testid="label-filter-loading-indicator">
                      {t('body.loading')}
                    </div>
                  </Dropdown>
                </div>
              ) : (
                <div className="w-full" ref={dropdownRef}>
                  <Dropdown
                    options={[]}
                    placeholder={
                      labelFlatItems.length > 0
                        ? t('body.labelsWithCount', {
                            count: labelFlatItems.length,
                          })
                        : t('body.selectLabels')
                    }
                    contentClassName={cn('z-50')}
                    alignment="start"
                    disabled={disabled}
                  >
                    <CheckboxTree
                      value={labelTreeItems}
                      setValue={setLabelTreeItems}
                      onChange={handleLabelsTreeChange}
                      style={
                        availableHeight
                          ? { maxHeight: `${availableHeight}px` }
                          : { maxHeight: '50dvh' }
                      }
                    />
                  </Dropdown>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </SlideOut.Body>
      <SlideOut.Footer>
        <Button
          disabled={!isDirty}
          size="large"
          variant="primary"
          type="submit"
          form={FORM_ID}
        >
          {t('actions.apply')}
        </Button>
      </SlideOut.Footer>
    </SlideOut>
  );
}

FilterPanel.displayName = 'FilterPane';
